import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useRole } from "../security/index";
import { Box, Typography, Button, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { ROUTES } from "./constants.js";
import {
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";

/**
 * RoleBasedRoute
 * Protects routes based on user groups and/or permissions
 *
 * Props:
 * - requiredGroups: array of GROUPS constants
 * - requiredPermissions: array of permission keys
 * - requireAll: true = must have all groups/permissions, false = any is sufficient
 * - fallback: component to render if access denied
 * - redirectTo: URL to redirect if access denied
 * - showAccessDenied: display a friendly access denied page
 * - allowSuperAdmin: bypass for superadmin/admins
 */
const RoleBasedRoute = ({
  children,
  requiredGroups = [],
  requiredPermissions = [],
  requireAll = false,
  fallback = null,
  redirectTo = "/unauthorized",
  showAccessDenied = true,
  allowSuperAdmin = true,
}) => {
  const { isAuthenticated, user } = useAuth();
  const { canAccessResource, isAdmin } = useRole();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Not authenticated → redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  //bypass
  if (allowSuperAdmin && isAdmin()) return children;

  const hasAccess = canAccessResource(
    requiredPermissions,
    requiredGroups,
    requireAll
  );

  if (!hasAccess) {
    if (fallback) {
      return fallback;
    }
    if (redirectTo && !showAccessDenied) {
      return <Navigate to={redirectTo} replace />;
    }
    if (showAccessDenied) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            textAlign: "center",
            p: 3,
          }}
        >
          <LockIcon sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            {t("errors.accessDenied", "Access Denied")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t(
              "errors.insufficientPermissions",
              "You don't have permission to access this resource."
            )}
          </Typography>
          <Alert severity="warning" sx={{ mb: 3, maxWidth: 400 }}>
            {requiredGroups.length > 0 && (
              <Typography variant="body2">
                Required groups: {requiredGroups.join(", ")}
              </Typography>
            )}
            {requiredPermissions.length > 0 && (
              <Typography variant="body2">
                Required permissions: {requiredPermissions.join(", ")}
              </Typography>
            )}
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTES.PROJECTS)}
          >
            {t("common.goBack", "Go Back")}
          </Button>
        </Box>
      );
    }
    return <Navigate to={redirectTo} replace />;
  }
  return children;
};

RoleBasedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredGroups: PropTypes.arrayOf(PropTypes.string),
  requiredPermissions: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
  fallback: PropTypes.node,
  redirectTo: PropTypes.string,
  showAccessDenied: PropTypes.bool,
  allowSuperAdmin: PropTypes.bool,
};

export default RoleBasedRoute;
